'use client';
import { useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Crown,
  Lightbulb,
  TrendingUp,
  Rocket,
  Twitter,
  Linkedin,
  Mail,
  ArrowRight,
  ChevronRight,
} from 'lucide-react';

// Animated logo component
function AnimatedLogo({ isVisible }: { isVisible: boolean }) {
  return (
    <div className="flex items-center gap-4 group">
      {/* PV Monogram */}
      <div className="relative">
        {/* Glow effect */}
        <div
          className={cn(
            'absolute inset-0 rounded-xl bg-gradient-to-br from-purple-500 to-violet-600 blur-xl opacity-0 transition-opacity duration-500 group-hover:opacity-40'
          )}
        />

        <svg
          className="w-12 h-12 relative z-10 transition-transform duration-300 group-hover:scale-105"
          viewBox="0 0 48 48"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          {/* P shape */}
          <path
            d="M8 8h12c6.627 0 12 5.373 12 12s-5.373 12-12 12H14v8H8V8z"
            fill="hsl(274 66% 32%)"
          />
          <path
            d="M14 14h6c3.314 0 6 2.686 6 6s-2.686 6-6 6h-6v-12z"
            fill="hsl(274 87% 12%)"
          />
          {/* V checkmark with animation */}
          <path
            d="M20 28l8 10 14-20"
            stroke="url(#footerLogoGradient)"
            strokeWidth="4"
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
            className={cn(
              'transition-all duration-1000',
              isVisible ? 'stroke-dashoffset-0' : ''
            )}
            style={{
              strokeDasharray: 50,
              strokeDashoffset: isVisible ? 0 : 50,
              transitionDelay: '500ms',
            }}
          />
          <defs>
            <linearGradient
              id="footerLogoGradient"
              x1="20"
              y1="28"
              x2="42"
              y2="18"
              gradientUnits="userSpaceOnUse"
            >
              <stop stopColor="hsl(271 81% 56%)" />
              <stop offset="1" stopColor="hsl(290 75% 60%)" />
            </linearGradient>
          </defs>
        </svg>
      </div>

      <div>
        <p className="font-display font-semibold text-white text-xl leading-tight">
          Purple Venture
        </p>
        <p className="text-xs text-purple-300 tracking-[0.2em] uppercase">
          Studio
        </p>
      </div>
    </div>
  );
}

// Typing cursor component
function TypingCursor() {
  return (
    <span className="inline-block w-0.5 h-5 bg-purple-400 ml-1 animate-pulse" />
  );
}

const loginLinks = [
  {
    role: 'Founder',
    icon: <Rocket className="w-4 h-4" />,
    href: '/auth/login',
  },
  {
    role: 'Investor',
    icon: <TrendingUp className="w-4 h-4" />,
    href: '/auth/login',
  },
];

const footerLinks = {
  Platform: [
    { label: 'How It Works', href: '#how-it-works' },
    { label: 'For Founders', href: '#founders' },
    { label: 'Ecosystem', href: '#ecosystem' },
    { label: 'Apply Now', href: '#apply' },
  ],
  'Apply As': [
    { label: 'Founder', href: '/apply/founder' },
    { label: 'Investor', href: '/apply/investor' },
  ],
  Company: [
    { label: 'About Us', href: '/about' },
    { label: 'Pitch Deck', href: '/pitch-deck' },
    { label: 'Press', href: '/press' },
    { label: 'Outcomes', href: '/outcomes' },
  ],
  Legal: [
    { label: 'Privacy', href: '/privacy' },
    { label: 'Terms', href: '/terms' },
  ],
};

export default function FooterSection() {
  const [isVisible, setIsVisible] = useState(false);
  const [email, setEmail] = useState('');
  const footerRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setIsVisible(true);
        });
      },
      { threshold: 0.1 }
    );

    if (footerRef.current) observer.observe(footerRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <footer
      ref={footerRef}
      className="relative pt-20 pb-8 overflow-hidden"
      style={{
        background:
          'linear-gradient(180deg, hsl(274 87% 10%) 0%, hsl(274 87% 8%) 100%)',
      }}
    >
      {/* Grain texture */}
      <div
        className="absolute inset-0 opacity-[0.08] pointer-events-none"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
        }}
      />

      {/* Top gradient border */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-purple-500/50 to-transparent" />

      {/* Background watermark text */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <span
          className={cn(
            'absolute -bottom-20 -right-20 text-[25rem] font-display font-bold text-white/[0.015] leading-none whitespace-nowrap transition-all duration-1000',
            isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-20'
          )}
        >
          PV
        </span>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Login Section */}
        <div
          className={cn(
            'mb-16 p-8 rounded-2xl transition-all duration-700',
            'bg-white/[0.03] border border-white/10 backdrop-blur-sm',
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          )}
        >
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            <div>
              <h3 className="text-xl font-display font-semibold text-white mb-2">
                Already a member?
              </h3>
              <p className="text-purple-200/70">
                Sign in to access your dashboard.
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              {loginLinks.map((link) => (
                <a
                  key={link.role}
                  href={link.href}
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border border-white/10 bg-white/5 text-purple-200 hover:border-purple-400/50 hover:text-white hover:bg-white/10 transition-all text-sm font-medium group"
                >
                  <span className="transition-transform group-hover:scale-110">
                    {link.icon}
                  </span>
                  {link.role}
                  <ChevronRight className="w-3 h-3 opacity-0 -ml-1 group-hover:opacity-100 group-hover:ml-0 transition-all" />
                </a>
              ))}
            </div>
          </div>
        </div>

        {/* Main footer content - Asymmetric layout */}
        <div
          className={cn(
            'grid lg:grid-cols-[1.5fr_1fr] gap-16 mb-16 transition-all duration-700 delay-200',
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          )}
        >
          {/* Left: Brand story */}
          <div>
            <AnimatedLogo isVisible={isVisible} />

            <p className="mt-8 text-purple-200/80 leading-relaxed max-w-md text-lg">
              The decision engine for fundable ventures. A closed ecosystem
              where every interaction is structured, paid, and outcome-driven.
            </p>

            {/* Tagline with typing cursor */}
            <p className="mt-6 font-serif italic text-purple-300/90 text-xl flex items-center">
              "Designing fundable ventures"
              <TypingCursor />
            </p>

            {/* Social links */}
            <div className="flex gap-3 mt-8">
              {[
                {
                  icon: <Twitter className="w-4 h-4" />,
                  href: 'https://twitter.com/purpleventure',
                  label: 'Twitter',
                },
                {
                  icon: <Linkedin className="w-4 h-4" />,
                  href: 'https://linkedin.com/company/purpleventure',
                  label: 'LinkedIn',
                },
                {
                  icon: <Mail className="w-4 h-4" />,
                  href: 'mailto:hello@purpleventure.studio',
                  label: 'Email',
                },
              ].map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  target={social.label !== 'Email' ? '_blank' : undefined}
                  rel={
                    social.label !== 'Email' ? 'noopener noreferrer' : undefined
                  }
                  className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-purple-300 hover:bg-purple-600 hover:border-purple-500 hover:text-white transition-all"
                  aria-label={social.label}
                >
                  {social.icon}
                </a>
              ))}
            </div>

            {/* Newsletter */}
            <div className="mt-10 p-6 rounded-xl bg-white/[0.03] border border-white/10">
              <h4 className="font-semibold text-white mb-2">Stay informed</h4>
              <p className="text-sm text-purple-200/60 mb-4">
                Occasional updates about the Purple ecosystem.
              </p>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  if (email) {
                    // TODO: Integrate with newsletter service
                    alert("Thank you for subscribing! We'll keep you updated.");
                    setEmail('');
                  }
                }}
                className="flex gap-2"
              >
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  required
                  className="flex-1 px-4 py-2.5 rounded-lg bg-white/5 border border-white/10 text-white placeholder:text-purple-300/40 focus:outline-none focus:ring-2 focus:ring-purple-500/30 focus:border-purple-400/50 transition-all"
                />
                <Button
                  type="submit"
                  className="bg-gradient-to-r from-purple-600 to-violet-600 text-white px-4 hover:from-purple-500 hover:to-violet-500 transition-all"
                >
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </form>
            </div>
          </div>

          {/* Right: Links grid */}
          <div className="grid grid-cols-2 gap-x-8 gap-y-8 sm:grid-cols-2">
            {Object.entries(footerLinks).map(([category, links]) => (
              <div key={category}>
                <h4 className="font-semibold text-white mb-4 text-sm uppercase tracking-wider">
                  {category}
                </h4>
                <ul className="space-y-3">
                  {links.map((link) => (
                    <li key={link.label}>
                      <a
                        href={link.href}
                        className="text-purple-200/70 hover:text-white transition-colors inline-flex items-center gap-1 group"
                      >
                        {link.label}
                        <ChevronRight className="w-3 h-3 opacity-0 -ml-1 group-hover:opacity-100 group-hover:ml-0 transition-all" />
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

      </div>
    </footer>
  );
}
